/**
 * Represents the /organization-config/ API resource.
 */
import {
  AccountingSoftwareType,
  AllthenticateInviteMode,
  BarcodeFormat,
  CurrencyField,
  CustomCustomerFieldType,
  MailchimpGroupCategoryType,
  MailchimpSyncWindow,
  OrgPOSSoftware,
  PaymentProcessorFeePolicy,
  PaymentProcessorType,
  PrintPageSize,
  ProfileField,
  ReportingFirstDayOfWeek,
  TaxDisplayFormat,
} from './organization-config-enums';

export interface OrganizationConfigRevenueAccount {
  account_code: string;
  account_name: string;
  account_id: string;
}

export interface PotentialQuickbooksDepositAccount {
  account_name: string;
  account_id: string;
  account_type: string;
}

export interface PotentialXeroDepositAccount {
  account_code?: string;
  account_name: string;
  account_id: string;
}

export interface XeroTrackingCategoryOption {
  category_name: string;
  category_id: string;
  option_name: string;
  option_id: string;
}

export interface AccountingAccount {
  account_id: string;
  account_name: string;
  account_code: string;
  account_type: string;
}

export class OrganizationConfig {
  // Allow snake_case variables in order to align with API.
  /* tslint:disable:variable-name */
  currency: CurrencyField;
  price_includes_tax: boolean;
  tax_display_format: TaxDisplayFormat;
  logo_image: string | null;
  logo_height_px: number;
  logo_margin_top_px: number;
  logo_margin_bottom_px: number;
  select_document_page_text: string;
  customer_login_minimum_age: string;
  membership_minimum_age: number | null;
  membership_minimum_age_applies_to_prepaid: boolean;
  display_email_opt_in: boolean;
  email_opt_in_checkbox_text: string;
  email_opt_in_help_text: string;
  email_opt_in_is_selected_by_default: boolean;
  inbox_item_max_age_months: number | null;
  profile_contact_field: ProfileField;
  major_profile_field_1: ProfileField;
  major_profile_field_2: ProfileField;
  minor_profile_field_1: ProfileField;
  minor_profile_field_2: ProfileField;
  minor_profile_field_3: ProfileField;
  minor_profile_field_4: ProfileField;
  minor_profile_field_5: ProfileField;
  minor_profile_field_6: ProfileField;
  minor_profile_field_7: ProfileField;
  minor_profile_field_8: ProfileField;
  minor_profile_field_9: ProfileField;
  customer_facing_linking_instructions: string;
  staff_verification_linking_instructions: string;
  spouse_relation_allowed: boolean;
  partner_relation_allowed: boolean;
  parent_relation_allowed: boolean;
  child_relation_allowed: boolean;
  legal_guardian_relation_allowed: boolean;
  legal_ward_relation_allowed: boolean;
  guardian_relation_allowed: boolean;
  ward_relation_allowed: boolean;
  sibling_relation_allowed: boolean;
  other_relation_allowed: boolean;
  allow_entry_pass_linking: boolean;
  entry_pass_linking_instructions: string;
  check_in_sound_success_id: number;
  check_in_sound_success_url: string;
  check_in_sound_success_with_event_check_in_id: number;
  check_in_sound_success_with_event_check_in_url: string;
  check_in_sound_success_with_challenge_completed_id: number;
  check_in_sound_success_with_challenge_completed_url: string;
  check_in_sound_success_with_reverification_period_id: number;
  check_in_sound_success_with_reverification_period_url: string;
  check_in_sound_success_with_missing_proficiency_id: number;
  check_in_sound_success_with_missing_proficiency_url: string;
  check_in_sound_no_barcode_match_id: number;
  check_in_sound_no_barcode_match_url: string;
  check_in_sound_alerts_id: number;
  check_in_sound_alerts_url: string;
  check_in_sound_duplicate_id: number;
  check_in_sound_duplicate_url: string;
  check_in_sound_no_entry_method_id: number;
  check_in_sound_no_entry_method_url: string;
  check_out_sound_success_id: number;
  check_out_sound_success_url: string;
  check_out_sound_error_id: number;
  check_out_sound_error_url: string;
  google_analytics_4_measurement_id: string;
  google_analytics_user_data_enabled: boolean;
  ga4_measurement_protocol_api_secret: string;
  meta_pixel_id: string;
  meta_pixel_advanced_matching_enabled: boolean;
  meta_capi_access_token: string;
  meta_capi_sync_error: string;
  meta_capi_sync_error_timestamp: string | null;
  climber_app_embed_script: string;
  restrict_climber_app_embedding: boolean;
  climber_app_embed_domains: string[];
  revenue_accounts: OrganizationConfigRevenueAccount[];
  time_tracking_enabled: boolean;
  open_date_enabled: boolean;
  open_date: string;
  barcode_prefix: string;
  generate_barcodes_automatically: boolean;
  limit_generated_barcodes_by_age: boolean;
  minimum_barcode_age: number | null;
  pin_timeout_seconds: number;
  allow_pos_gift_card_usage: boolean;
  allow_offline_payments: boolean;
  require_membership_freeze_reason: boolean;
  require_membership_cancellation_reason: boolean;
  membership_freeze_reason_options: string[];
  membership_cancellation_reason_options: string[];
  reporting_first_day_of_week: ReportingFirstDayOfWeek;
  send_recurring_membership_payment_confirmation_emails: boolean;
  send_membership_max_freeze_soon_emails: boolean;
  failed_membership_cancellation_period_days: number | null;
  allow_climber_app_profile_photo_update: boolean;
  allow_climber_app_visit_history: boolean;
  display_remaining_capacity_in_climber_app_listings: boolean;
  enable_referral_codes: boolean;
  custom_style_primary_color_hex: string;
  custom_style_card_border_radius_px: number | null;
  custom_style_button_border_radius_px: number | null;
  check_in_barcode_format: BarcodeFormat;
  allow_gift_card_sales: boolean;
  external_gift_card_purchase_url: string;
  max_gift_card_future_delivery_date_days: number;
  gift_card_giver_terms_and_conditions: string;
  min_gift_card_value: string;
  max_gift_card_value: string;
  gift_card_tax_rate_id: number | null;
  gift_card_tax_rate_name: string | null;
  gift_card_recipient_text: string;
  time_zone: string;
  today_local: string;
  climber_app_base_url: string;
  pos_software: OrgPOSSoftware;
  has_mailchimp_integration: boolean;
  has_allthenticate_integration: boolean;
  allthenticate_invite_mode: AllthenticateInviteMode;
  quickbooks_deposit_account_id: string;
  quickbooks_deposit_account_name: string;
  mailchimp_audience_id: string;
  mailchimp_audience_name: string;
  mailchimp_audience_id_pending_sync: string;
  mailchimp_audience_sync_started_at?: string;
  mailchimp_sync_window: MailchimpSyncWindow;
  mailchimp_sync_transactional: boolean;
  mailchimp_sync_error: string;
  lightspeed_sync_error: string;
  square_sync_error: string;
  lightspeed_capitan_user_id: string;
  lightspeed_capitan_user_username: string;
  lightspeed_capitan_user_display_name: string;
  allow_printing_gifts: boolean;
  gift_print_paper_size: PrintPageSize;
  gift_print_logo_image: string | null;
  gift_center_alert_enabled: boolean;
  gift_center_alert_text: string;
  gift_center_alert_color_hex: string;
  gift_center_alert_start_date: string | null;
  gift_center_alert_end_date: string | null;
  gift_center_carousel_title: string;
  invite_email_additional_text: string;
  recaptcha_enabled: boolean;
  customer_search_recent_activity_window_months: number | null;
  lapsed_customer_guest_pass_window_months: number;
  customer_profile_sidebar_order: string[];
  customer_profile_action_button_order: string[];
  /* tslint:enable:variable-name */

  getClimberAppNavbarStyle() {
    const style: any = {};

    if (this.custom_style_primary_color_hex.length === 6) {
      style['background-color'] = '#' + this.custom_style_primary_color_hex;
    }

    return style;
  }

  getClimberAppLogoStyle() {
    const style: any = {
      'margin-bottom': '20px',
      'margin-top': '20px'
    };

    if (this.logo_margin_bottom_px !== null) {
      style['margin-bottom'] = this.logo_margin_bottom_px.toString() + 'px';
    }
    if (this.logo_margin_top_px !== null) {
      style['margin-top'] = this.logo_margin_top_px.toString() + 'px';
    }

    return style;
  }

  getClimberAppCapitanCardStyle() {
    const style: any = {};

    if (this.custom_style_primary_color_hex.length === 6) {
      style.border = '1px solid #' + this.custom_style_primary_color_hex;
    }
    if (this.custom_style_card_border_radius_px !== null) {
      style['border-radius'] = this.custom_style_card_border_radius_px.toString() + 'px';
    }

    return style;
  }

  getClimberAppCapitanCardHeaderStyle() {
    const style: any = {};

    if (this.custom_style_primary_color_hex.length === 6) {
      style.background = '#' + this.custom_style_primary_color_hex;
    }
    if (this.custom_style_card_border_radius_px !== null) {
      let innerRadius = (this.custom_style_card_border_radius_px - 2).toString() + 'px';
      if (this.custom_style_card_border_radius_px <= 1) {
        innerRadius = '0';
      }

      style['border-radius'] = innerRadius + ' ' + innerRadius + ' 0 0';
    }

    return style;
  }

  getClimberAppCapitanBtnStyle(isHovering: boolean) {
    const style: any = {};

    if (this.custom_style_primary_color_hex.length === 6) {
      style['border-color'] = '#' + this.custom_style_primary_color_hex;
      if (isHovering) {
        style['background-color'] = '#' + this.custom_style_primary_color_hex;
      } else {
        style.color = '#' + this.custom_style_primary_color_hex;
      }
    }
    if (this.custom_style_button_border_radius_px !== null) {
      style['border-radius'] = this.custom_style_button_border_radius_px.toString() + 'px';
    }

    return style;
  }
}

export interface MailchimpGroup {
  id: number;
  interest_id: string;
  name: string;
  display_order: number;
}

export interface MailchimpGroupCategory {
  id: number;
  interest_category_id: string;
  name: string;
  display_order: number;
  group_type: MailchimpGroupCategoryType;
  groups: MailchimpGroup[];
}

export interface CustomCustomerField {
  id: number;
  url: string;
  name: string;
  field_type: CustomCustomerFieldType;
  options: string[];
  created_at: string;
  updated_at: string;
}

export interface AccountingConnection {
  id: number;
  url: string;
  name: string;
  time_zone: string | null;
  accounting_software: AccountingSoftwareType;
  payment_processor_fee_policy: PaymentProcessorFeePolicy;
  revenue_accounts: OrganizationConfigRevenueAccount[];
  default_membership_revenue_account_code: string;
  default_entry_pass_revenue_account_code: string;
  default_event_revenue_account_code: string;
  default_voucher_pack_revenue_account_code: string;
  gift_card_revenue_account_code: string;
  quickbooks_deposit_account_id: string;
  quickbooks_deposit_account_name: string;
  xero_deposit_account_id: string;
  xero_deposit_account_code: string;
  xero_deposit_account_name: string;
  payment_processor_fee_account_id: string;
  payment_processor_fee_account_code: string;
  payment_processor_fee_account_name: string;
  location_ids: number[];
  location_names: string[];
  created_at: string;
  updated_at: string;
}

export interface PaymentProcessorConnection {
  id: number;
  url: string;
  name: string;
  payment_processor: PaymentProcessorType;
  stripe_user_id: string;
  gravity_oid: string;
  mx_merchant_merchant_id: string;
  mx_merchant_consumer_api_key: string;
  mx_merchant_dba: string;
  bank_account_payments_enabled: boolean;
  require_bank_account_payments_for_recurring_memberships: boolean;
  location_ids: number[];
  location_names: string[];
  created_at: string;
  updated_at: string;
}

export interface SquareCustomerGroup {
  id: number;
  name: string;
  is_managed_by_capitan: boolean;
}

export interface LightspeedCustomerGroup {
  id: number;
  name: string;
  lightspeed_group_identifier: string;
  is_managed_by_capitan: boolean;
  rank: number;
}

export function getPOSSoftwareDisplay(sw: OrgPOSSoftware): string {
  switch (sw) {
    case OrgPOSSoftware.none:
      return 'None';
    case OrgPOSSoftware.square:
      return 'Square';
    case OrgPOSSoftware.lightspeed:
      return 'Lightspeed';
    default:
      return 'Unknown';
  }
}

export function getAccountingSoftwareDisplay(sw: AccountingSoftwareType): string {
  switch (sw) {
    case AccountingSoftwareType.none:
      return 'Manual';
    case AccountingSoftwareType.quickbooksOnline:
      return 'Quickbooks Online';
    case AccountingSoftwareType.xero:
      return 'Xero';
    default:
      return 'Unknown';
  }
}

export function getPaymentProcessorTypeDisplay(pp: PaymentProcessorType): string {
  switch (pp) {
    case PaymentProcessorType.stripe:
      return 'Stripe';
    case PaymentProcessorType.gravity:
      return 'Gravity';
    case PaymentProcessorType.mxMerchant:
      return 'Priority / MX Merchant';
    case PaymentProcessorType.mock:
      return 'Mock';
    default:
      return 'Unknown';
  }
}

export function getCustomCustomerFieldTypeDisplay(ft: CustomCustomerFieldType): string {
  switch (ft) {
    case CustomCustomerFieldType.checkboxes:
      return 'Checkboxes';
    case CustomCustomerFieldType.dropdown:
      return 'Dropdown';
    case CustomCustomerFieldType.radio:
      return 'Radio';
    case CustomCustomerFieldType.singleLineText:
      return 'Single-Line Text';
    case CustomCustomerFieldType.multipleLineText:
      return 'Multiple-Line Text';
    default:
      return 'Unknown';
  }
}
